import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, TrendingUp, DollarSign, Clock, Users, AlertTriangle } from 'lucide-react';
import '../../../styles/admin/production/ProfitabilityDashboard.css';

const ProfitabilityDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateFilter, setDateFilter] = useState('today');
  const [customDate, setCustomDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, [dateFilter, customDate, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = '/api/production/profitability/dashboard';
      const params = new URLSearchParams();
      
      if (dateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('date', today);
      } else if (dateFilter === 'custom' && customDate) {
        params.append('date', customDate);
      } else if (dateFilter === 'range' && startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching profitability:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${((value || 0) * 100).toFixed(1)}%`;
  };

  const getMarginColor = (margin) => {
    if (margin >= 0.4) return 'excellent';
    if (margin >= 0.2) return 'good';
    if (margin >= 0) return 'low';
    return 'negative';
  };

  if (loading) {
    return (
      <div className="profitability-dashboard">
        <div className="loading">Chargement des données de rentabilité...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="profitability-dashboard">
        <div className="error">Erreur lors du chargement des données</div>
      </div>
    );
  }

  const { kpis, top_routes, unprofitable_routes, top_employees } = data;

  return (
    <div className="profitability-dashboard">
      <div className="page-header">
        <div className="header-left">
          <Link to="/admin/production/dashboard" className="back-button">
            <ArrowLeft size={20} />
            Retour au Dashboard
          </Link>
          <h1>📊 Rentabilité & Performance</h1>
        </div>
      </div>

      {/* Filtres */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Période:</label>
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
            <option value="today">Aujourd'hui</option>
            <option value="custom">Date spécifique</option>
            <option value="range">Plage de dates</option>
          </select>
        </div>

        {dateFilter === 'custom' && (
          <div className="filter-group">
            <label>Date:</label>
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
            />
          </div>
        )}

        {dateFilter === 'range' && (
          <>
            <div className="filter-group">
              <label>Du:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Au:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </>
        )}

        <button className="btn-primary" onClick={fetchData}>
          Actualiser
        </button>
      </div>

      {/* KPIs Globaux */}
      <div className="kpis-grid">
        <div className="kpi-card revenue">
          <div className="kpi-icon">
            <DollarSign size={28} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Revenu Total</div>
            <div className="kpi-value">{formatCurrency(kpis.total_revenue)}</div>
          </div>
        </div>

        <div className="kpi-card cost">
          <div className="kpi-icon">
            <TrendingUp size={28} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Coût Total</div>
            <div className="kpi-value">{formatCurrency(kpis.total_cost)}</div>
            <div className="kpi-detail">
              MO: {formatCurrency(kpis.total_labor_cost)} | 
              Produits: {formatCurrency(kpis.total_product_cost)}
            </div>
          </div>
        </div>

        <div className={`kpi-card profit ${kpis.gross_profit < 0 ? 'negative' : ''}`}>
          <div className="kpi-icon">
            <DollarSign size={28} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Profit Brut</div>
            <div className="kpi-value">{formatCurrency(kpis.gross_profit)}</div>
            <div className="kpi-detail">
              Marge: {formatPercent(kpis.margin_percent)}
            </div>
          </div>
        </div>

        <div className="kpi-card efficiency">
          <div className="kpi-icon">
            <Clock size={28} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Performance</div>
            <div className="kpi-value">{formatCurrency(kpis.profit_per_hour)}/h</div>
            <div className="kpi-detail">
              Revenu/h: {formatCurrency(kpis.revenue_per_hour)}
            </div>
          </div>
        </div>

        <div className="kpi-card hours">
          <div className="kpi-icon">
            <Users size={28} />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Volume</div>
            <div className="kpi-value">{kpis.total_routes} routes</div>
            <div className="kpi-detail">
              {kpis.total_hours.toFixed(1)} heures
            </div>
          </div>
        </div>
      </div>

      {/* Top Routes */}
      <div className="section">
        <h2>🏆 Routes Rentables</h2>
        {top_routes.length === 0 ? (
          <p className="no-data">Aucune route rentable pour cette période</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Route</th>
                  <th>Date</th>
                  <th>Stops</th>
                  <th>Revenu</th>
                  <th>Coût MO</th>
                  <th>Coût Produits</th>
                  <th>Profit</th>
                  <th>Marge</th>
                </tr>
              </thead>
              <tbody>
                {top_routes.map((route) => (
                  <tr key={route.route_id}>
                    <td>
                      <Link to={`/admin/production/routes/${route.route_id}`}>
                        {route.route_name || route.route_number}
                      </Link>
                    </td>
                    <td>{route.route_date}</td>
                    <td>{route.completed_stops}/{route.total_stops}</td>
                    <td>{formatCurrency(route.total_revenue)}</td>
                    <td>{formatCurrency(route.total_labor_cost)}</td>
                    <td>{formatCurrency(route.total_product_cost)}</td>
                    <td className="profit">{formatCurrency(route.gross_profit)}</td>
                    <td>
                      <span className={`badge margin-${getMarginColor(route.margin_percent)}`}>
                        {formatPercent(route.margin_percent)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Routes Non Rentables */}
      {unprofitable_routes.length > 0 && (
        <div className="section alert">
          <h2>
            <AlertTriangle size={24} />
            Routes Non Rentables
          </h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Route</th>
                  <th>Date</th>
                  <th>Stops</th>
                  <th>Revenu</th>
                  <th>Coût Total</th>
                  <th>Perte</th>
                  <th>Marge</th>
                </tr>
              </thead>
              <tbody>
                {unprofitable_routes.map((route) => (
                  <tr key={route.route_id} className="negative-row">
                    <td>
                      <Link to={`/admin/production/routes/${route.route_id}`}>
                        {route.route_name || route.route_number}
                      </Link>
                    </td>
                    <td>{route.route_date}</td>
                    <td>{route.completed_stops}/{route.total_stops}</td>
                    <td>{formatCurrency(route.total_revenue)}</td>
                    <td>{formatCurrency(route.total_cost)}</td>
                    <td className="loss">{formatCurrency(route.gross_profit)}</td>
                    <td>
                      <span className="badge margin-negative">
                        {formatPercent(route.margin_percent)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Employés */}
      <div className="section">
        <h2>👨‍🌾 Performance Employés</h2>
        {top_employees.length === 0 ? (
          <p className="no-data">Aucune donnée employé pour cette période</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employé</th>
                  <th>Heures</th>
                  <th>Coût</th>
                  <th>Revenu Généré</th>
                  <th>Profit Généré</th>
                  <th>Revenu/h</th>
                  <th>Profit/h</th>
                  <th>Stops</th>
                </tr>
              </thead>
              <tbody>
                {top_employees.map((emp) => (
                  <tr key={emp.employee_id}>
                    <td>{emp.employee_name}</td>
                    <td>{emp.hours.toFixed(1)}h</td>
                    <td>{formatCurrency(emp.cost)}</td>
                    <td>{formatCurrency(emp.revenue)}</td>
                    <td className={emp.profit < 0 ? 'loss' : 'profit'}>
                      {formatCurrency(emp.profit)}
                    </td>
                    <td>{formatCurrency(emp.revenue_per_hour)}</td>
                    <td className={emp.profit_per_hour < 0 ? 'loss' : 'profit'}>
                      {formatCurrency(emp.profit_per_hour)}
                    </td>
                    <td>{emp.stops}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfitabilityDashboard;
